// useNovaNudges — subscribes to public.nova_nudges via realtime and
// surfaces new undismissed nudges as sonner toasts. Mount once at the
// app root (below the Toaster).

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface NudgeRow {
  id: string;
  nudge_type: string;
  priority: string;
  title: string;
  body: string | null;
  action_url: string | null;
  dismissed_at: string | null;
  shown_at: string | null;
  created_at: string;
}

export function useNovaNudges() {
  const { user } = useAuth();
  const shownRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;

    const showNudge = (n: NudgeRow) => {
      if (n.dismissed_at || shownRef.current.has(n.id)) return;
      shownRef.current.add(n.id);
      const fn = n.priority === "high" ? toast.warning : toast;
      fn(n.title, {
        description: n.body ?? undefined,
        duration: n.priority === "high" ? 10000 : 6000,
        action: n.action_url
          ? { label: "Open", onClick: () => { window.location.href = n.action_url!; } }
          : undefined,
        onDismiss: () => {
          supabase.from("nova_nudges")
            .update({ dismissed_at: new Date().toISOString() })
            .eq("id", n.id)
            .then(() => {});
        },
      });
      supabase.from("nova_nudges")
        .update({ shown_at: new Date().toISOString() })
        .eq("id", n.id)
        .then(() => {});
    };

    // 1. Initial fetch — any undismissed, unshown nudges from the last hour
    const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    supabase.from("nova_nudges")
      .select("*")
      .is("dismissed_at", null)
      .is("shown_at", null)
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(5)
      .then(({ data }) => {
        (data ?? []).forEach((n) => showNudge(n as NudgeRow));
      });

    // 2. Realtime — new inserts
    const channel = supabase
      .channel("nova-nudges")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "nova_nudges" },
        (payload) => showNudge(payload.new as NudgeRow),
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);
}